import { qsa, qs, listen, add, remove } from '@fluorescent/dom';
import { emit, on } from '@/glow/events';
import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase.js';
import ProductItem from '@/lib/product-item';

gsap.registerPlugin(CustomEase);

const selectors = {
  slider: '[data-slider]',
  prev: '[data-prev]',
  next: '[data-next]',
};

export default (node, type, opts = {}) => {
  const sliderElement = qs(selectors.slider, node);
  const previousButton = qs(selectors.prev, node);
  const nextButton = qs(selectors.next, node);
  let slider = null;
  let productItem = null;
  const events = [];

  const defaultOpts = {
    adaptiveHeight: !window.matchMedia('(min-width: 45em)').matches,
    cellAlign: 'left',
    cellSelector: '[data-slide]',
    pageDots: false,
    prevNextButtons: false,
    contain: true,
    imagesLoaded: true,
    pauseAutoPlayOnHover: !window.Shopify.designMode,
    on: {
      ready: function () {
        _updateNavigationPosition();
        productItem = ProductItem(node);
        this.resize();
      },
    },
  };

  const config = Object.assign({}, defaultOpts, opts);

  if (!config.wrapAround) {
    previousButton.disabled = true;
  }

  const _updateNavigationPosition = () => {
    const firstImage = qs('.image__img, .placeholder-image', node);
    const firstImageHeight = firstImage.clientHeight;
    const buttonHeight = nextButton.clientHeight;
    const halfFirstImageHeight = firstImageHeight / 2;
    const halfButtonHeight = buttonHeight / 2;

    const offset = halfFirstImageHeight - halfButtonHeight;

    node.style.setProperty('--navigation-offset', `${offset}px`);

    remove(nextButton, 'hidden');
    remove(previousButton, 'hidden');
  };

  import('flickity').then(({ default: Flickity }) => {
    slider = new Flickity(sliderElement, config);

    if (!config.wrapAround) {
      slider.on('scroll', progress => {
        const progressScale = progress * 100;

        // https://github.com/metafizzy/flickity/issues/289
        previousButton.disabled = progressScale < 1;
        nextButton.disabled = progressScale > 99;
      });
    }

    // Account for page-transition links that can fire on flickity drag
    slider.on('dragStart', () =>
      slider.slider.childNodes.forEach(slide =>
        qsa('a[href]', slide).forEach(element => add(element, 'no-transition'))
      )
    );
    slider.on('dragEnd', () =>
      setTimeout(() => {
        slider.slider.childNodes.forEach(slide =>
          qsa('a[href]', slide).forEach(element =>
            remove(element, 'no-transition')
          )
        );
      }, 100)
    );

    emit(`${type}:initialized`);
  });

  const prevClick = listen(previousButton, 'click', handleClick);
  const nextClick = listen(nextButton, 'click', handleClick);
  const prevMouseenter = listen(previousButton, 'mouseenter', arrowMouseenter);
  const nextMouseenter = listen(nextButton, 'mouseenter', arrowMouseenter);

  const arrowClickEase = gsap.parseEase('0.33,0.00,0.00,1.00');
  const arrowHoverEase = gsap.parseEase('0.33,0.00,0.00,1.00');

  function handleClick(evt) {
    const prev = evt.target.closest(selectors.prev);
    const next = evt.target.closest(selectors.next);

    if (prev) {
      slider && slider.previous();
      arrowClick(prev);
    }
    if (next) {
      slider && slider.next();
      arrowClick(next);
    }
  }

  function arrowClick(button) {
    gsap.to(button, {
      keyframes: [{ scale: 0.8 }, { scale: 1 }],
      duration: 0.33,
      ease: arrowClickEase,
    });
  }

  function arrowMouseenter(evt) {
    const svg = qs('svg', evt.target);
    gsap.to(svg, {
      keyframes: [
        { x: 15, opacity: 0 },
        { x: -15, duration: 0 },
        { x: 0, opacity: 1 },
      ],
      duration: 0.43,
      ease: arrowHoverEase,
    });
  }

  return {
    destroy: () => {
      if ((slider.slides || {}).length > 1) {
        prevClick();
        nextClick();
        prevMouseenter();
        nextMouseenter();
      }
      slider && slider.destroy();
      events.forEach(unsubscribe => unsubscribe());
      productItem && productItem.unload();
    },
    select: index => {
      if (slider) {
        slider.select(index);
      } else {
        events.push(on(`${type}:initialized`, () => slider.select(index)));
      }
    },
  };
};
